/**
 * StreetViewPanel Component
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-engine
 *
 * @description
 * Panel modal que muestra Google Street View para una ubicación específica.
 * Integrado con el sistema de diseño WESTEER (glassmorphism, dark theme).
 *
 * @remarks
 * **Características**:
 * - Modal fullscreen con glassmorphism
 * - Integración con Google Maps Street View API
 * - Botón de cierre con símbolo >>
 * - Posición inicial basada en coordenadas del mapa
 * - Escapable con tecla ESC
 *
 * **Requisitos**:
 * - Google Maps API Key configurada en VITE_GOOGLE_MAPS_API_KEY
 * - APIs habilitadas: Maps JavaScript API, Street View Static API
 *
 * @example
 * ```tsx
 * <StreetViewPanel
 *   isOpen={showStreetView}
 *   onClose={() => setShowStreetView(false)}
 *   lat={-34.603722}
 *   lng={-58.381592}
 * />
 * ```
 */

import { useEffect, useRef } from 'react';
import './street-view-panel.css';

/**
 * Props del componente StreetViewPanel
 */
export interface StreetViewPanelProps {
  /**
   * Controla si el panel está visible
   */
  isOpen: boolean;

  /**
   * Callback cuando se cierra el panel
   */
  onClose: () => void;

  /**
   * Latitud de la ubicación a mostrar
   */
  lat: number;

  /**
   * Longitud de la ubicación a mostrar
   */
  lng: number;
}

/**
 * Componente StreetViewPanel
 *
 * @remarks
 * Carga dinámicamente Google Maps JavaScript API y renderiza Street View.
 * El panel es modal y ocupa toda la pantalla con glassmorphism.
 */
export function StreetViewPanel({
  isOpen,
  onClose,
  lat,
  lng,
}: StreetViewPanelProps): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);

  /**
   * Carga Google Maps API dinámicamente
   */
  useEffect(() => {
    if (!isOpen) return;

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.error(
        '[StreetViewPanel] VITE_GOOGLE_MAPS_API_KEY no configurada en .env'
      );
      return;
    }

    // Verificar si ya está cargada
    if (window.google?.maps) {
      initStreetView();
      return;
    }

    // Cargar Google Maps API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('[StreetViewPanel] Google Maps API cargada');
      initStreetView();
    };

    script.onerror = () => {
      console.error('[StreetViewPanel] Error al cargar Google Maps API');
    };

    document.head.appendChild(script);

    return () => {
      // No removemos el script porque puede ser usado por otros componentes
    };
  }, [isOpen]);

  /**
   * Inicializa Street View con la ubicación proporcionada
   */
  const initStreetView = (): void => {
    if (!containerRef.current) return;
    if (!window.google?.maps) return;

    try {
      const panorama = new google.maps.StreetViewPanorama(
        containerRef.current,
        {
          position: { lat, lng },
          pov: {
            heading: 0,
            pitch: 0,
          },
          zoom: 1,
          addressControl: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
        }
      );

      panoramaRef.current = panorama;

      console.log('[StreetViewPanel] Street View inicializado:', { lat, lng });
    } catch (error) {
      console.error('[StreetViewPanel] Error al inicializar Street View:', error);
    }
  };

  /**
   * Actualiza posición cuando cambian las coordenadas
   */
  useEffect(() => {
    if (!isOpen || !panoramaRef.current) return;

    panoramaRef.current.setPosition({ lat, lng });
  }, [isOpen, lat, lng]);

  /**
   * Maneja tecla ESC para cerrar
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // No renderizar si está cerrado
  if (!isOpen) return null;

  return (
    <div
      className="street-view-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Vista de calle"
    >
      <div
        className="street-view-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con botón cerrar */}
        <header className="street-view-header">
          <h2 className="street-view-title">Street View</h2>
          <button
            type="button"
            className="street-view-close-btn"
            onClick={onClose}
            aria-label="Cerrar Street View"
            title="Cerrar Street View (ESC)"
          >
            {/* Símbolo >> de WESTEER */}
            <svg
              viewBox="0 0 88 88"
              fill="currentColor"
              className="close-icon"
              aria-hidden="true"
            >
              {/* Primer chevron */}
              <polygon points="0,5 34,40 0,75 22,75 56,40 22,5" />
              {/* Segundo chevron */}
              <polygon points="32,5 66,40 32,75 54,75 88,40 54,5" />
            </svg>
          </button>
        </header>

        {/* Contenedor de Street View */}
        <div
          ref={containerRef}
          className="street-view-container"
        />
      </div>
    </div>
  );
}
